import * as React from "react";
import { css } from "@emotion/core";
import { Button, Icon } from "@blueprintjs/core";
import { useNavigate, useParams } from "react-router-dom";
import { useSinkState, useSinkActions } from "context/sink";
import {
  zonedTimeToUtc,
  getTimezoneOffset,
  formatInTimeZone,
} from "date-fns-tz";
import { useFolderOrderTypes } from "hooks";
import { addMilliseconds } from "date-fns";
import { OrderMachineContext } from "../state/orderMachine";
import { GeometryMachineContext } from "../state/geometryMachine";
import ApiSurveys from "api/surveys";
import ApiOrders, { isOrderSubmitted } from "api/orders";
import ApiLayers from "api/layers";
import ApiAnnotations from "api/annotations";
import * as turf from "@turf/helpers";
import { getGeometryDisplayName } from "../sections/SelectUploads/utils";
import { useCountdown } from "../hooks";
import { formatCountdown } from "utils/format";
import {
  useUploadMachine,
  useUploadMachineSelector,
} from "../context/upload-machine";

export default function Complete() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { surveys, selectedSurveyId, sink } = useSinkState();
  const { orders, folders } = sink;
  const { getLayers, resink } = useSinkActions();

  const order = orders.find((order) => order.survey_id === selectedSurveyId);

  const submitted = React.useRef(isOrderSubmitted(order));

  const [scheduledAt, setScheduledAt] = React.useState<Date | null>(
    order?.scheduled_at ? new Date(order.scheduled_at) : new Date()
  );
  const [isCancelling, setIsCancelling] = React.useState(false);

  const [isSubmitted, setIsSubmitted] = React.useState(isOrderSubmitted(order));
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const survey = surveys.find((survey) => survey.id === selectedSurveyId);

  const site = sink.sites?.find((s) => s.id === survey?.site_id);

  const folder = folders.find((folder) => folder.id === survey?.folder_id);

  const orderTypeId = OrderMachineContext.useSelector(
    (state) => state.context.orderTypeId
  );
  const folderId = survey?.folder_id;

  const { data: orderTypes } = useFolderOrderTypes(folderId);

  const uploadedImageCount = useUploadMachineSelector(
    (state) => state.context.imageUploads.length
  );
  const uploadedLidarCount = useUploadMachineSelector(
    (state) => state.context.lidarUploads.length
  );
  const uploadedGeometryCount = useUploadMachineSelector(
    (state) => state.context.geometryUploads.length
  );

  const uploadMachine = useUploadMachine();

  const remainingSeconds = useCountdown(scheduledAt);

  const geometryUploads = GeometryMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const timezone = OrderMachineContext.useSelector(
    (state) => state.context.timezone
  );
  const center = OrderMachineContext.useSelector(
    (state) => state.context.center
  );
  const zoom = OrderMachineContext.useSelector((state) => state.context.zoom);
  const startTime = OrderMachineContext.useSelector(
    (state) => state.context.startTime
  );
  const endTime = OrderMachineContext.useSelector(
    (state) => state.context.endTime
  );
  const description = OrderMachineContext.useSelector(
    (state) => state.context.description
  );

  const autoSubmit = OrderMachineContext.useSelector(
    (state) => state.context.autoSubmit
  );

  const convertToSiteTime = React.useCallback(
    (date: string) => {
      if (!date) return null;

      const target = new Date(date);

      const siteOffset = getTimezoneOffset(timezone, date);
      const localOffset = getTimezoneOffset(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        date
      );

      const offsetDate = addMilliseconds(target, localOffset - siteOffset);

      return zonedTimeToUtc(
        offsetDate,
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    },
    [timezone]
  );

  const createGeometryAnnotations = React.useCallback(async () => {
    if (!survey?.id || !geometryUploads.length) return [];

    const layers = [];

    for (const geometry of geometryUploads) {
      if (!geometry.data?.collection?.features?.length) continue;
      if (geometry.data.collection.features.length > 25) continue;

      const annotationLabel = getGeometryDisplayName(geometry);

      try {
        const layer = await ApiLayers.createAnnotationLayer(survey.id, {
          label: annotationLabel,
        });

        await getLayers(survey.id);

        console.log(geometry.data.collection.features);

        const annotationsToCreate = geometry.data.collection.features
          .filter((feature) => feature.geometry)
          .map((feature) => ({
            label: feature.geometry.type,
            geometry: feature.geometry,
            properties: feature.properties || {},
          }));

        if (annotationsToCreate.length > 0) {
          await ApiAnnotations.createAnnotations(
            layer.id,
            annotationsToCreate,
            survey.id
          );
        }

        layers.push(layer);
      } catch (error) {
        console.error(
          `Failed to create annotations for geometry ${annotationLabel}:`,
          error
        );
      }
    }

    return layers;
  }, [getLayers, survey?.id, geometryUploads]);

  const orderSubmitted = isOrderSubmitted(order);

  const submitOrder = React.useCallback(
    async (source: "auto" | "manual") => {
      if (isSubmitting) return;

      await resink();

      if (orderSubmitted) {
        submitted.current = true;
        setIsSubmitted(true);
        return;
      }

      if (submitted.current) return;

      try {
        setIsSubmitting(true);

        const nominal = convertToSiteTime(startTime);
        const start = convertToSiteTime(startTime);
        const end = convertToSiteTime(endTime);

        await createGeometryAnnotations();

        await ApiSurveys.updateSurvey(survey.id, {
          nominal_flight_date: nominal && nominal.toISOString(),
          start_time: start && start.toISOString(),
          end_time: end && end.toISOString(),
          timezone,
          location: JSON.stringify(
            turf.point([parseFloat(center[0]), parseFloat(center[1])]).geometry
          ),
          zoom_start: Math.round(zoom),
          log: `submitOrder: ${timezone}, ${center[0]}, ${center[1]}, ${zoom}`,
        });

        const result = await ApiOrders.updateOrder(survey.id, {
          description,
          files: [],
          source,
        });

        await resink();

        if (result.order?.scheduled_at) {
          setScheduledAt(new Date(result.order.scheduled_at));
        }

        submitted.current = true;
        setIsSubmitted(true);
      } catch (error) {
        console.error("Error during order submission:", error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      startTime,
      endTime,
      timezone,
      center,
      zoom,
      survey,
      description,
      convertToSiteTime,
      createGeometryAnnotations,
      resink,
      orderSubmitted,
    ]
  );

  React.useEffect(() => {
    if (autoSubmit && isSubmitted === false) {
      submitOrder("auto");
    }
  }, [submitOrder, autoSubmit, isSubmitted]);

  const handleGotoSurveyFolder = () => {
    navigate(`/folders/${folder?.id}`);
  };

  const handleCancelOrder = async () => {
    if (!order?.id || !survey?.id) return;

    setIsCancelling(true);
    try {
      await ApiOrders.cancelScheduledOrder(survey.id, order.id);
      navigate(`/surveys/${id}`);
    } catch (error) {
      console.error("Error cancelling order:", error);
      setIsCancelling(false);
    }
  };

  const handleBack = () => {
    uploadMachine.send({ type: "BACK" });
  };

  const isScheduled = remainingSeconds > 0;

  const orderTypeLabel = orderTypes?.find((t) => t.id === orderTypeId)?.name;

  const formatOrderDateTime = (date: string | null) => {
    if (!date) return "—";
    return formatInTimeZone(new Date(date), timezone, "MMM d, yyyy h:mm a zzz");
  };

  const showSubmittedUi = autoSubmit || isSubmitted;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem 2rem;
        text-align: center;
        height: 100%;
        background: linear-gradient(
          135deg,
          ${isScheduled ? "var(--blue-6)" : "var(--success-6)"} 0%,
          ${isScheduled ? "var(--blue-5)" : "var(--success-5)"} 100%
        );
        border-radius: 0.5rem;
        color: var(--neutral-1);
      `}
    >
      <div
        css={css`
          margin-bottom: 2rem;
        `}
      >
        <Icon
          icon="tick-circle"
          intent="success"
          size={80}
          css={css`
            color: var(--neutral-1);
            margin-bottom: 1rem;
          `}
        />
        <h2
          css={css`
            font-size: 1.8rem;
            font-weight: 700;
            margin: 0 0 2rem 0;
            color: var(--neutral-1);
          `}
        >
          {showSubmittedUi ? "Order Submitted" : "Upload Completed"}
        </h2>
        <p
          css={css`
            font-size: 1.1rem;
            margin: 0 0 0.5rem 0;
            color: var(--neutral-2);
            max-width: 678px;
            line-height: 1.5;
          `}
        >
          {isScheduled
            ? `The order has been submitted, but you can still cancel it to make modifications.`
            : `Your survey order has been successfully submitted for "${survey?.label}".`}
        </p>
        {isScheduled && (
          <p
            css={css`
              font-size: 1.25rem;
              margin: 0 0 0.5rem 0;
              color: var(--neutral-3);
              font-weight: 700;
              line-height: 1.5;
            `}
          >
            All files have been uploaded.
          </p>
        )}
        {!isScheduled && (
          <p
            css={css`
              font-size: 1.1rem;
              margin: 0 0 0.5rem 0;
              color: var(--neutral-2);
              max-width: 600px;
              line-height: 1.5;
            `}
          >
            All files have been uploaded.
          </p>
        )}
      </div>
      {showSubmittedUi && (
        <div
          css={css`
            width: 100%;
            max-width: 640px;
            margin: 0 0 2rem 0;
            padding: 1.25rem 1.5rem;
            border-radius: 0.5rem;
            background: rgba(0, 0, 0, 0.15);
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
            text-align: left;
          `}
        >
          <h3
            css={css`
              font-size: 0.85rem;
              font-weight: 600;
              letter-spacing: 0.05em;
              text-transform: uppercase;
              margin: 0 0 1rem 0;
              color: var(--neutral-2);
            `}
          >
            Summary
          </h3>
          <div
            css={css`
              display: grid;
              grid-template-columns: 16px 100px 1fr;
              gap: 0.5rem 0.75rem;
              font-size: 0.95rem;
              line-height: 1.4;
              align-items: baseline;
            `}
          >
            <Icon icon="cube" size={14} css={summaryIconCss} />
            <span css={summaryLabelCss}>Order type</span>
            <span css={summaryValueCss}>{orderTypeLabel || "—"}</span>

            {site && (
              <>
                <Icon icon="map-marker" size={14} css={summaryIconCss} />
                <span css={summaryLabelCss}>Site</span>
                <span css={summaryValueCss}>{site.label}</span>
              </>
            )}

            <Icon icon="time" size={14} css={summaryIconCss} />
            <span css={summaryLabelCss}>Start</span>
            <span css={summaryValueCss}>{formatOrderDateTime(startTime)}</span>

            <Icon icon="time" size={14} css={summaryIconCss} />
            <span css={summaryLabelCss}>End</span>
            <span css={summaryValueCss}>{formatOrderDateTime(endTime)}</span>

            <Icon icon="annotation" size={14} css={summaryIconCss} />
            <span css={summaryLabelCss}>Description</span>
            <span css={summaryValueCss}>{description?.trim() || "—"}</span>
          </div>
          <div
            css={css`
              margin-top: 1rem;
              padding-top: 0.75rem;
            `}
          >
            <span
              css={css`
                font-size: 0.8rem;
                font-weight: 500;
                color: var(--neutral-3);
                display: block;
                margin-bottom: 0.5rem;
              `}
            >
              Uploaded
            </span>
            <div
              css={css`
                display: flex;
                gap: 0.5rem;
              `}
            >
              {(
                [
                  { icon: "media", label: "Images", count: uploadedImageCount },
                  {
                    icon: "antenna",
                    label: "Point Clouds",
                    count: uploadedLidarCount,
                  },
                  {
                    icon: "shapes",
                    label: "Layers & Accessories",
                    count: uploadedGeometryCount,
                  },
                ] as const
              ).map((tile) => (
                <div
                  key={tile.label}
                  css={css`
                    flex: 1 1 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 1rem 0.75rem;
                    border-radius: 0.375rem;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                  `}
                >
                  <span
                    css={css`
                      font-size: 0.8rem;
                      color: var(--neutral-3);
                    `}
                  >
                    {tile.label}
                  </span>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 0.25rem;
                    `}
                  >
                    <Icon
                      icon={tile.icon}
                      size={18}
                      css={css`
                        color: var(--neutral-3);
                      `}
                    />
                    <span
                      css={css`
                        font-size: 1.25rem;
                        font-weight: 700;
                        color: var(--neutral-2);
                      `}
                    >
                      {tile.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div
        css={css`
          display: flex;
          gap: 1rem;
        `}
      >
        {showSubmittedUi ? (
          <>
            {isScheduled && (
              <Button
                intent="danger"
                size="large"
                onClick={handleCancelOrder}
                loading={isCancelling}
                disabled={isCancelling}
                icon="cube-remove"
              >
                Cancel Order (
                <span
                  css={css`
                    font-variant-numeric: tabular-nums;
                  `}
                >
                  {formatCountdown(remainingSeconds)}
                </span>
                )
              </Button>
            )}
            <Button
              intent="primary"
              size="large"
              onClick={handleGotoSurveyFolder}
              icon="folder-close"
            >
              {folder?.label} Folder
            </Button>
          </>
        ) : (
          <>
            <Button
              intent="danger"
              size="large"
              onClick={handleBack}
              icon="cube-remove"
            >
              Back
            </Button>
            <Button
              intent="primary"
              size="large"
              onClick={() => {
                setIsSubmitted(true);
                submitOrder("manual");
              }}
              icon="send-to"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Submit Order
            </Button>
          </>
        )}
        {showSubmittedUi && survey?.id && (
          <Button
            intent="primary"
            icon="map"
            size="large"
            onClick={() => navigate(`/surveys/${survey.id}`)}
          >
            View survey
          </Button>
        )}
      </div>
    </div>
  );
}

const summaryIconCss = css`
  color: var(--neutral-3);
`;

const summaryLabelCss = css`
  margin: 0;
  font-weight: 500;
  color: var(--neutral-2);
`;

const summaryValueCss = css`
  margin: 0;
  font-weight: 600;
  color: var(--neutral-1);
  overflow-wrap: anywhere;
`;
